import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { AppVersion } from '@/components/layout/app-version'
import { Sidebar } from '@/components/layout/sidebar'
import { ThemeToggle, UnitsToggle } from '@/components/layout/toolbar'
import { DialogOverlay } from '@/components/ui/dialog'

/**
 * The activity list as a slide-in panel from the left, for viewports below
 * `md` -- where a sidebar laid out beside (or stacked above) the workspace
 * would leave the map, dashboard and timeline almost no room on a phone.
 * Opened from the toolbar's "Activities" button; closes on Escape, a tap
 * outside, its close button, or after picking an activity.
 *
 * Also holds the display-unit and theme toggles, which the toolbar drops
 * on narrow viewports to fit on one row, and the app version.
 */
export function SidebarDrawer({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogOverlay />
        <DialogPrimitive.Content
          aria-modal="true"
          aria-describedby={undefined}
          className="border-border bg-card text-card-foreground fixed inset-y-0 left-0 z-50 flex w-[min(20rem,85vw)] flex-col border-r shadow-lg focus:outline-none"
        >
          <div className="border-border flex items-center justify-between border-b px-3 py-2">
            <DialogPrimitive.Title className="text-sm font-semibold">
              FIT Editor
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              aria-label="Close activities"
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring rounded-sm p-2 outline-none focus-visible:ring-2"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          <Sidebar
            className="min-h-0 w-full flex-1 border-r-0"
            onActivitySelected={() => onOpenChange(false)}
            showVersion={false}
          />
          <div className="border-border flex flex-col gap-2 border-t px-3 py-2">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-muted-foreground">Display</span>
              <div className="flex items-center gap-2">
                <UnitsToggle />
                <ThemeToggle />
              </div>
            </div>
            <AppVersion />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
